import { DateTime } from "luxon";
import { Enums } from "./enums";
import {
  isAutomationRuleComment,
  messageAuthorDisplayNameForComment,
  _truncatedCommentPreview,
  _convertMarkdownToHTMLForComments,
  type TicketRecentCommentSnapshot,
} from "../entity-service/ticket-entity-service";
import { ticketCommentEntity } from "../model/entity/ticket-comment-entity";

/** Sidebar recent-comment types from `cx/comment/recent` (Messages, Approval, TPC). */
export const SIDEBAR_MESSAGE_COMMENT_TYPES = new Set([
  "Comment",
  "ApprovalComment",
  "ThirdPartyConversation",
]);

export function isSidebarPublicMessageCommentType(
  type: string | null | undefined,
): boolean {
  const t = String(type ?? "");
  return t === "" || SIDEBAR_MESSAGE_COMMENT_TYPES.has(t);
}

function isoUtcToMillis(iso: string | null | undefined): number {
  if (iso == null || String(iso).trim() === "") {
    return 0;
  }
  const dt = DateTime.fromISO(String(iso), { zone: "utc" });
  return dt.isValid ? dt.toMillis() : 0;
}

function ruleIdFromLastModifiedJson(
  lm: Record<string, unknown>,
): string | undefined {
  const direct = lm.ruleId != null ? String(lm.ruleId).trim() : "";
  if (direct !== "" && direct !== "null") {
    return direct;
  }
  if (lm.byAutomation !== true) {
    return undefined;
  }
  const external = lm.externalCreatedByModel;
  if (external == null || typeof external !== "object") {
    return undefined;
  }
  const createdById = String(
    (external as Record<string, unknown>).createdById ?? "",
  ).trim();
  return createdById !== "" && createdById !== "null" ? createdById : undefined;
}

function isSidebarPublicMessageSnapshot(
  rc: TicketRecentCommentSnapshot,
): boolean {
  if (rc.isInternal === true) {
    return false;
  }
  return isSidebarPublicMessageCommentType(rc.type);
}

/** Optimistic / failed portal posts must not drive sidebar preview or sort. */
export function isDeliveredTicketComment(
  row: { isSuccess?: boolean } | null | undefined,
): boolean {
  return row == null || row.isSuccess !== false;
}

function isSidebarPublicCommentRow(row: ticketCommentEntity): boolean {
  if (row.isInternal) {
    return false;
  }
  if (row.isDeleted) {
    return false;
  }
  if (!isDeliveredTicketComment(row)) {
    return false;
  }
  return isSidebarPublicMessageCommentType(row.type);
}

/** Latest successful sidebar comment on a ticket (newest `createdOn` first). */
export function findLastDeliveredPublicCommentOnTicket(
  ticketId: string,
): ticketCommentEntity | null {
  const tid = String(ticketId ?? "").trim();
  if (!tid) {
    return null;
  }

  const rows = ticketCommentEntity
    .query()
    .where((c: ticketCommentEntity) => String(c.ticketId ?? "") === tid)
    .all()
    .filter(isSidebarPublicCommentRow);

  if (rows.length === 0) {
    return null;
  }

  rows.sort((a, b) => {
    const ma = isoUtcToMillis(a.createdOn);
    const mb = isoUtcToMillis(b.createdOn);
    return mb - ma;
  });
  return rows[0] ?? null;
}

function hasUnreadAttribution(
  snapshot: TicketRecentCommentSnapshot | null | undefined,
): boolean {
  if (snapshot == null) {
    return false;
  }
  return (
    Number(snapshot.createdByTechnicianId ?? 0) > 0 ||
    Number(snapshot.createdByContactId ?? 0) > 0 ||
    isAutomationRuleComment(snapshot.ruleId, snapshot.ruleName)
  );
}

function pickNonEmptySnapshotText(
  ...values: (string | null | undefined)[]
): string | null | undefined {
  for (const value of values) {
    if (value != null && String(value).trim() !== "") {
      return value;
    }
  }
  return values.find((value) => value != null);
}

/** Merge `lastModifiedJson` / ORM snapshot with cx/recent when ids align but attribution is missing. */
function enrichUnreadContextSnapshot(
  base: TicketRecentCommentSnapshot,
  richer: TicketRecentCommentSnapshot,
): TicketRecentCommentSnapshot {
  return {
    ...base,
    createdByTechnicianId:
      Number(base.createdByTechnicianId ?? 0) > 0
        ? base.createdByTechnicianId
        : richer.createdByTechnicianId,
    createdByContactId:
      Number(base.createdByContactId ?? 0) > 0
        ? base.createdByContactId
        : richer.createdByContactId,
    ruleId: isAutomationRuleComment(base.ruleId, base.ruleName)
      ? base.ruleId
      : richer.ruleId ?? base.ruleId,
    ruleName: base.ruleName ?? richer.ruleName,
    type: richer.type ?? base.type,
    createdOn: richer.createdOn || base.createdOn,
    comment: pickNonEmptySnapshotText(richer.comment, base.comment),
    markdownComment: pickNonEmptySnapshotText(
      richer.markdownComment,
      base.markdownComment,
    ),
    markdownWithoutSignature: pickNonEmptySnapshotText(
      richer.markdownWithoutSignature,
      base.markdownWithoutSignature,
    ),
  };
}

function snapshotFromCommentRow(
  row: ticketCommentEntity,
  commentId: string,
): TicketRecentCommentSnapshot {
  return {
    commentId: String(row.id ?? commentId),
    ticketId: row.ticketId,
    comment: row.comment,
    markdownComment: row.markdownComment,
    markdownWithoutSignature: row.markdownWithoutSignature,
    type: row.type,
    isInternal: row.isInternal,
    createdOn: row.createdOn,
    createdByContactId: row.createdByContactId,
    createdByTechnicianId: row.createdByTechnicianId,
    ruleId: row.ruleId,
    ruleName: row.ruleName,
    tick: row.tick,
  };
}

/** UTC millis from `lastModifiedJson.on` (Approval/TPC/status edits). */
export function lastModifiedJsonActivityMillis(
  lastModifiedJson?: string | null,
): number {
  const lm = parseLastModifiedRecord(lastModifiedJson);
  if (lm?.on == null) {
    return 0;
  }
  return isoUtcToMillis(String(lm.on));
}

function parseLastModifiedRecord(
  lastModifiedJson?: string | null,
): Record<string, unknown> | null {
  if (lastModifiedJson == null || String(lastModifiedJson).trim() === "") {
    return null;
  }
  try {
    const parsed = JSON.parse(String(lastModifiedJson));
    return parsed != null && typeof parsed === "object"
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** Visibility / field edits — not a new Messages-tab post (Approval/TPC often use `IsPublic`). */
export function isCommentVisibilityLastModified(
  lastModifiedJson?: string | null,
): boolean {
  const lm = parseLastModifiedRecord(lastModifiedJson);
  return lm?.editedType === 3 && lm?.toValue === "IsPublic";
}

function isApprovalOrTpcCommentType(type: string | null | undefined): boolean {
  const t = String(type ?? "").trim();
  return t === "ApprovalComment" || t === "ThirdPartyConversation";
}

/**
 * Approval / TPC tab activity must not mark the Messages sidebar unread or bump the badge.
 * Uses Messages-tab `recentComment` preview only (`cx/comment/recent`), keyed by ORM lookups
 * on `recentComment.commentId` — never from aggregate latest-activity id on the ticket row.
 */
export function isNonMessagesTabActivityForClientUnread(
  lastModifiedJson?: string | null,
  recentComment?: TicketRecentCommentSnapshot | null,
): boolean {
  const lookupId = String(recentComment?.commentId ?? "").trim();

  let ormRow = null as ticketCommentEntity | null;
  if (lookupId !== "") {
    ormRow = ticketCommentEntity.query().whereId(lookupId).first() ?? null;
    if (ormRow != null && isApprovalOrTpcCommentType(ormRow.type)) {
      return true;
    }
    if (
      ormRow != null &&
      isSidebarPublicMessageCommentType(ormRow.type) &&
      isDeliveredTicketComment(ormRow)
    ) {
      return false;
    }
  }

  if (recentComment != null && isApprovalOrTpcCommentType(recentComment.type)) {
    return true;
  }

  if (isCommentVisibilityLastModified(lastModifiedJson)) {
    const lm = parseLastModifiedRecord(lastModifiedJson);
    const lmHasBody = lm?.comment != null && String(lm.comment).trim() !== "";
    if (!lmHasBody && !isDeliveredTicketComment(ormRow)) {
      return true;
    }
    return false;
  }

  return false;
}

/**
 * Logged-in portal contact posted on Approval / TPC — suppress new unread for them only.
 * Other mapped contacts (e.g. TPC CC) must still mark the ticket unread for everyone else.
 */
export function isContactOriginatedNonMessagesTabActivity(
  lastModifiedJson?: string | null,
  recentComment?: TicketRecentCommentSnapshot | null,
  viewerContactId?: number,
): boolean {
  if (
    !isNonMessagesTabActivityForClientUnread(lastModifiedJson, recentComment)
  ) {
    return false;
  }
  const viewerId = Number(viewerContactId ?? 0);
  if (viewerId <= 0) {
    return false;
  }
  const ctx = resolveUnreadContextRecentComment({
    lastModifiedJson,
    recentComment,
    ticketId: recentComment?.ticketId ?? null,
  });
  if (Number(ctx?.createdByTechnicianId ?? 0) > 0) {
    return false;
  }
  const authorContactId = Number(ctx?.createdByContactId ?? 0);
  if (authorContactId > 0) {
    return authorContactId === viewerId;
  }
  const lm = parseLastModifiedRecord(lastModifiedJson);
  if (lm?.byTech === true) {
    return false;
  }
  const by = Number(lm?.by ?? 0);
  return by > 0 && by === viewerId;
}

/** Technician (or other contact) Approval/TPC — may mark Messages unread. */
export function isInboundTechnicianNonMessagesActivity(
  lastModifiedJson?: string | null,
  recentComment?: TicketRecentCommentSnapshot | null,
  viewerContactId?: number,
): boolean {
  return (
    isNonMessagesTabActivityForClientUnread(lastModifiedJson, recentComment) &&
    !isContactOriginatedNonMessagesTabActivity(
      lastModifiedJson,
      recentComment,
      viewerContactId,
    )
  );
}

function pickNewerPublicCommentSnapshot(
  a: TicketRecentCommentSnapshot | null,
  b: TicketRecentCommentSnapshot | null,
): TicketRecentCommentSnapshot | null {
  const ma = isoUtcToMillis(a?.createdOn);
  const mb = isoUtcToMillis(b?.createdOn);
  if (ma <= 0 && mb <= 0) {
    return a ?? b ?? null;
  }
  if (mb <= 0) {
    return a ?? null;
  }
  if (ma <= 0) {
    return b ?? null;
  }
  return ma >= mb ? a : b;
}

/** Prefer newest snapshot that has sidebar body text (markdown or HTML). */
function pickBestSidebarPreviewSnapshot(
  ...candidates: (TicketRecentCommentSnapshot | null | undefined)[]
): TicketRecentCommentSnapshot | null {
  const valid = candidates.filter(
    (candidate): candidate is TicketRecentCommentSnapshot =>
      candidate != null && isSidebarPublicMessageSnapshot(candidate),
  );
  if (valid.length === 0) {
    return null;
  }

  const withBody = valid.filter(snapshotHasSidebarDisplayBody);
  const pool = withBody.length > 0 ? withBody : valid;
  return pool.reduce<TicketRecentCommentSnapshot | null>(
    (best, current) => pickNewerPublicCommentSnapshot(best, current) ?? current,
    null,
  );
}

function commentBodyPlainText(comment: string | null | undefined): string {
  if (comment == null || String(comment).trim() === "") {
    return "";
  }
  return normalizePlainPreviewText(
    String(comment)
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
      .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, "")
      .replace(/<[^>]+>/g, "")
      .replace(/&amp;nbsp;/gi, " ")
      .replace(/&zwnj;/gi, ""),
  );
}

function decodeHtmlEntities(value: string): string {
  const namedEntities: Record<string, string> = {
    amp: "&",
    apos: "'",
    gt: ">",
    hellip: "\u2026",
    ldquo: "\u201c",
    lsquo: "\u2018",
    lt: "<",
    mdash: "\u2014",
    nbsp: " ",
    ndash: "\u2013",
    quot: '"',
    rdquo: "\u201d",
    rsquo: "\u2019",
  };

  let decoded = value;
  for (let i = 0; i < 2; i += 1) {
    const next = decoded.replace(
      /&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]+);/gi,
      (entity, body: string) => {
        const lower = body.toLowerCase();
        if (lower.startsWith("#x")) {
          const codePoint = Number.parseInt(lower.slice(2), 16);
          return Number.isFinite(codePoint) &&
            codePoint >= 0 &&
            codePoint <= 0x10ffff
            ? String.fromCodePoint(codePoint)
            : entity;
        }
        if (lower.startsWith("#")) {
          const codePoint = Number.parseInt(lower.slice(1), 10);
          return Number.isFinite(codePoint) &&
            codePoint >= 0 &&
            codePoint <= 0x10ffff
            ? String.fromCodePoint(codePoint)
            : entity;
        }
        return namedEntities[lower] ?? entity;
      },
    );
    if (next === decoded) {
      break;
    }
    decoded = next;
  }

  return decoded;
}

function normalizePlainPreviewText(value: string): string {
  return decodeHtmlEntities(value)
    .replace(/\u00a0/g, " ")
    .replace(/\u200c/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function markdownBodyPlainText(markdown: string | null | undefined): string {
  if (markdown == null || String(markdown).trim() === "") {
    return "";
  }
  const md = String(markdown);
  try {
    const html = _convertMarkdownToHTMLForComments(md);
    return commentBodyPlainText(html);
  } catch {
    return normalizePlainPreviewText(md);
  }
}

const IMAGE_ONLY_SIDEBAR_PREVIEW_LABEL = "(image)";

const MARKDOWN_IMAGE_PATTERN = /!\[[^\]]*\]\([^)]*\)/;

function stripMarkdownImages(markdown: string): string {
  return markdown
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\s+/g, "")
    .trim();
}

function markdownIsImageOnly(markdown: string | null | undefined): boolean {
  const trimmed = markdown != null ? String(markdown).trim() : "";
  if (trimmed === "" || !MARKDOWN_IMAGE_PATTERN.test(trimmed)) {
    return false;
  }
  return stripMarkdownImages(trimmed) === "";
}

function htmlIsImageOnly(html: string | null | undefined): boolean {
  const trimmed = html != null ? String(html).trim() : "";
  if (trimmed === "" || !/<img\b/i.test(trimmed)) {
    return false;
  }
  return commentBodyPlainText(trimmed) === "";
}

function snapshotIsImageOnly(
  rc: TicketRecentCommentSnapshot | null | undefined,
): boolean {
  if (rc == null) {
    return false;
  }
  const markdown = pickNonEmptySnapshotText(
    rc.markdownWithoutSignature,
    rc.markdownComment,
  );
  if (markdown != null && markdownIsImageOnly(markdown)) {
    return true;
  }
  return rc.comment != null && htmlIsImageOnly(rc.comment);
}

/** Plain text from snapshot: prefers markdown body so inbound email entities render cleanly. */
function snapshotPlainBody(
  rc: TicketRecentCommentSnapshot | null | undefined,
): string {
  if (rc == null) {
    return "";
  }
  if (snapshotIsImageOnly(rc)) {
    return IMAGE_ONLY_SIDEBAR_PREVIEW_LABEL;
  }
  const fromMarkdown = markdownBodyPlainText(
    rc.markdownWithoutSignature ?? rc.markdownComment,
  );
  if (fromMarkdown !== "") {
    return fromMarkdown;
  }
  const fromComment = rc.comment != null ? String(rc.comment).trim() : "";
  if (fromComment !== "") {
    return commentBodyPlainText(rc.comment);
  }
  return "";
}

function snapshotHasSidebarDisplayBody(
  rc: TicketRecentCommentSnapshot | null | undefined,
): boolean {
  return snapshotPlainBody(rc) !== "";
}

/** Latest-activity snapshot from `lastModifiedJson` (includes Approval posts with `IsPublic`). */
function snapshotFromLastModifiedForLatestActivity(
  ticket: {
    ticketId?: string | null;
    lastModifiedJson?: string | null;
    updatedByTechnicianId?: number | null;
  },
  commentId: string,
): TicketRecentCommentSnapshot | null {
  const raw = ticket.lastModifiedJson;
  if (raw == null || String(raw).trim() === "") {
    return null;
  }
  const lm = parseLastModifiedRecord(raw);
  const hasCommentBody =
    lm?.comment != null && String(lm.comment).trim() !== "";
  if (isCommentVisibilityLastModified(raw) && !hasCommentBody) {
    return null;
  }
  try {
    const createdOn = lm?.on != null ? String(lm.on) : "";
    const comment = lm?.comment != null ? String(lm.comment) : null;
    if (!createdOn && (comment == null || comment.trim() === "")) {
      return null;
    }
    const by = Number(lm?.by ?? 0);
    const byTech = lm?.byTech === true;
    const byAutomation = lm?.byAutomation === true;
    const ruleId = lm != null ? ruleIdFromLastModifiedJson(lm) : undefined;
    let createdByTechnicianId: number | null = byTech && by > 0 ? by : null;
    if (createdByTechnicianId == null && byAutomation) {
      const updatedBy = Number(ticket.updatedByTechnicianId ?? 0);
      if (updatedBy > 0) {
        createdByTechnicianId = updatedBy;
      }
    }
    return {
      commentId: String(commentId),
      ticketId: ticket.ticketId ?? null,
      comment,
      type: "Comment",
      isInternal: false,
      createdOn: createdOn || undefined,
      createdByContactId: !byTech && by > 0 ? by : null,
      createdByTechnicianId,
      ruleId,
    };
  } catch {
    return null;
  }
}

/** Minimal snapshot from `lastModifiedJson` when SignalR pushes activity before ORM/API hydrate. */
function snapshotFromTicketLastModifiedJson(
  ticket: {
    ticketId?: string | null;
    lastModifiedJson?: string | null;
  },
  commentId: string,
): TicketRecentCommentSnapshot | null {
  const ormRow = ticketCommentEntity.query().whereId(commentId).first();
  if (ormRow != null && !isSidebarPublicCommentRow(ormRow)) {
    return null;
  }

  const raw = ticket.lastModifiedJson;
  if (raw == null || String(raw).trim() === "") {
    return null;
  }
  if (isCommentVisibilityLastModified(raw)) {
    return null;
  }
  try {
    const lm = JSON.parse(String(raw)) as Record<string, unknown>;
    const createdOn = lm.on != null ? String(lm.on) : "";
    const comment = lm.comment != null ? String(lm.comment) : null;
    if (!createdOn && (comment == null || comment.trim() === "")) {
      return null;
    }
    const by = Number(lm.by ?? 0);
    const byTech = lm.byTech === true;
    const ruleId = ruleIdFromLastModifiedJson(lm);
    return {
      commentId: String(commentId),
      ticketId: ticket.ticketId ?? null,
      comment,
      type: "Comment",
      isInternal: false,
      createdOn: createdOn || undefined,
      createdByContactId: !byTech && by > 0 ? by : null,
      createdByTechnicianId: byTech && by > 0 ? by : null,
      ruleId,
    };
  } catch {
    return null;
  }
}

/** Build `recentComment` for partial ticket rows from `ticketPrivateCommentAdded` / tick payloads. */
export function buildRecentCommentSnapshotFromSignalRCommentMessage(
  message: Record<string, unknown>,
): TicketRecentCommentSnapshot | null {
  const commentId = String(message.commentId ?? "").trim();
  const ticketId = String(message.entityId ?? message.ticketId ?? "").trim();
  if (!commentId || !ticketId) {
    return null;
  }

  let createdOn = message.updatedOn != null ? String(message.updatedOn) : "";
  let comment: string | null = null;
  let markdownComment: string | null = null;
  let markdownWithoutSignature: string | null = null;
  let createdByTechnicianId: number | null = null;
  let createdByContactId: number | null = null;
  let ruleId: string | undefined;

  const rawLm = message.lastModifiedJson;
  const visibilityOnlyEdit =
    rawLm != null && isCommentVisibilityLastModified(String(rawLm));
  if (rawLm != null && String(rawLm).trim() !== "") {
    try {
      const lm = JSON.parse(String(rawLm)) as Record<string, unknown>;
      if (lm.on != null) {
        createdOn = String(lm.on);
      }
      if (lm.comment != null) {
        comment = String(lm.comment);
      }
      const by = Number(lm.by ?? 0);
      if (lm.byTech === true && by > 0) {
        createdByTechnicianId = by;
      } else if (by > 0) {
        createdByContactId = by;
      }
      ruleId = ruleIdFromLastModifiedJson(lm);
    } catch {
      /* use updatedOn only */
    }
  }

  if (!createdOn.trim()) {
    return null;
  }

  const ormRow = ticketCommentEntity.query().whereId(commentId).first();
  if (ormRow != null && !isSidebarPublicCommentRow(ormRow)) {
    return null;
  }

  if (
    ormRow?.markdownComment != null &&
    String(ormRow.markdownComment).trim() !== ""
  ) {
    markdownComment = String(ormRow.markdownComment);
  } else {
    const fromMsg = message.markdownComment ?? message.MarkdownComment;
    if (fromMsg != null && String(fromMsg).trim() !== "") {
      markdownComment = String(fromMsg);
    }
  }
  if (
    ormRow?.markdownWithoutSignature != null &&
    String(ormRow.markdownWithoutSignature).trim() !== ""
  ) {
    markdownWithoutSignature = String(ormRow.markdownWithoutSignature);
  } else {
    const fromMsg =
      message.markdownWithoutSignature ?? message.MarkdownWithoutSignature;
    if (fromMsg != null && String(fromMsg).trim() !== "") {
      markdownWithoutSignature = String(fromMsg);
    }
  }

  const type = String(
    ormRow?.type ?? message.type ?? message.commentType ?? message.Type ?? "",
  ).trim();
  if (!isSidebarPublicMessageCommentType(type)) {
    return null;
  }

  const snapshot: TicketRecentCommentSnapshot = {
    commentId,
    ticketId,
    comment,
    markdownComment,
    markdownWithoutSignature,
    type: type || "Comment",
    isInternal: false,
    createdOn,
    createdByContactId,
    createdByTechnicianId,
    ruleId,
    tick: message.tick != null ? Number(message.tick) : undefined,
  };

  if (visibilityOnlyEdit && !isDeliveredTicketComment(ormRow)) {
    return null;
  }

  if (!createdOn.trim() && !isDeliveredTicketComment(ormRow)) {
    return null;
  }

  return snapshot;
}

export function resolveTicketRecentCommentSnapshot(ticket: {
  ticketId?: string | null;
  recentComment?: TicketRecentCommentSnapshot | null;
  lastModifiedJson?: string | null;
}): TicketRecentCommentSnapshot | null {
  const lookupId = String(ticket.recentComment?.commentId ?? "").trim();
  const ormRow =
    lookupId !== ""
      ? ticketCommentEntity.query().whereId(lookupId).first()
      : null;

  const ormDelivered =
    ormRow != null && isDeliveredTicketComment(ormRow) ? ormRow : null;

  if (ticket.recentComment != null) {
    const fromTicket = ticket.recentComment;
    if (ormDelivered != null) {
      const merged = {
        ...fromTicket,
        commentId: String(fromTicket.commentId ?? ormDelivered.id ?? lookupId),
        ruleId: fromTicket.ruleId ?? ormDelivered.ruleId,
        ruleName: fromTicket.ruleName ?? ormDelivered.ruleName,
        createdOn: fromTicket.createdOn ?? ormDelivered.createdOn,
        comment: pickNonEmptySnapshotText(
          fromTicket.comment,
          ormDelivered.comment,
        ),
        markdownComment: pickNonEmptySnapshotText(
          fromTicket.markdownComment,
          ormDelivered.markdownComment,
        ),
        markdownWithoutSignature: pickNonEmptySnapshotText(
          fromTicket.markdownWithoutSignature,
          ormDelivered.markdownWithoutSignature,
        ),
        type: ormDelivered.type ?? fromTicket.type,
        isInternal:
          fromTicket.isInternal === true || ormDelivered.isInternal === true,
        createdByContactId:
          fromTicket.createdByContactId ?? ormDelivered.createdByContactId,
        createdByTechnicianId:
          fromTicket.createdByTechnicianId ??
          ormDelivered.createdByTechnicianId,
      };
      if (isSidebarPublicMessageSnapshot(merged)) {
        return merged;
      }
    } else if (
      isDeliveredTicketComment(ormRow) &&
      isSidebarPublicMessageSnapshot(fromTicket)
    ) {
      return fromTicket;
    }
    if (
      isDeliveredTicketComment(fromTicket as { isSuccess?: boolean }) &&
      isSidebarPublicMessageSnapshot(fromTicket)
    ) {
      return fromTicket;
    }
  }

  if (ormDelivered != null) {
    const fromOrm = snapshotFromCommentRow(
      ormDelivered,
      String(ormDelivered.id),
    );
    if (isSidebarPublicMessageSnapshot(fromOrm)) {
      return fromOrm;
    }
  }

  if (lookupId !== "") {
    const fromLastModified = snapshotFromTicketLastModifiedJson(
      ticket,
      lookupId,
    );
    if (fromLastModified != null) {
      return fromLastModified;
    }
  }

  const ticketId = String(
    ticket.ticketId ?? ormRow?.ticketId ?? ticket.recentComment?.ticketId ?? "",
  );
  const fallback = findLastDeliveredPublicCommentOnTicket(ticketId);
  if (fallback != null) {
    return snapshotFromCommentRow(fallback, String(fallback.id));
  }

  return null;
}

/**
 * Snapshot for unread / read fingerprint — driven by Messages-tab `recentComment`
 * (`cx/comment/recent`) and `lastModifiedJson`, not aggregate latest activity id alone.
 */
export function resolveUnreadContextRecentComment(ticket: {
  ticketId?: string | null;
  recentComment?: TicketRecentCommentSnapshot | null;
  lastModifiedJson?: string | null;
  updatedByTechnicianId?: number | null;
}): TicketRecentCommentSnapshot | null {
  const lookupId = String(ticket.recentComment?.commentId ?? "").trim();
  let resolved: TicketRecentCommentSnapshot | null = null;

  if (lookupId !== "") {
    const ormRow = ticketCommentEntity.query().whereId(lookupId).first();
    if (ormRow != null && isDeliveredTicketComment(ormRow)) {
      resolved = snapshotFromCommentRow(ormRow, lookupId);
    }
    if (resolved == null) {
      const fromLm = snapshotFromLastModifiedForLatestActivity(
        ticket,
        lookupId,
      );
      if (fromLm != null) {
        resolved = fromLm;
      }
    }
  }

  if (ticket.recentComment != null) {
    const api = ticket.recentComment;
    const apiId = String(api.commentId ?? "").trim();
    if (resolved == null) {
      return api;
    }
    if (apiId !== "" && apiId === lookupId) {
      return enrichUnreadContextSnapshot(resolved, api);
    }
    if (!hasUnreadAttribution(resolved) && hasUnreadAttribution(api)) {
      return api;
    }
  }

  if (resolved != null) {
    return resolved;
  }

  return resolveTicketRecentCommentSnapshot(ticket);
}

/** UTC millis for client read fingerprint (Messages tab only when latest activity is TPC/Approval). */
export function ticketLatestActivityMsForClientRead(ticket: {
  recentComment?: TicketRecentCommentSnapshot | null;
  recentCommentId?: string | null;
  lastModifiedJson?: string | null;
  createdOn?: string | null;
  jsCreatedOn?: Date | null;
  updatedOn?: string | null;
}): number {
  const publicMessageMs = ticketRecentMessageCreatedOnMillis(ticket);
  const latestCommentId = String(ticket.recentCommentId ?? "").trim();
  const messagesRecentCommentId = String(
    ticket.recentComment?.commentId ?? "",
  ).trim();
  if (
    latestCommentId !== "" &&
    (messagesRecentCommentId === "" ||
      messagesRecentCommentId !== latestCommentId)
  ) {
    return Math.max(publicMessageMs, ticketCreatedOnSortMillis(ticket));
  }

  const nonMessagesLatest = isNonMessagesTabActivityForClientUnread(
    ticket.lastModifiedJson,
    ticket.recentComment,
  );
  if (nonMessagesLatest) {
    return Math.max(publicMessageMs, ticketCreatedOnSortMillis(ticket));
  }
  const fromLm = lastModifiedJsonActivityMillis(ticket.lastModifiedJson);
  const fromActivity = isoUtcToMillis(
    resolveUnreadContextRecentComment(ticket)?.createdOn,
  );
  return Math.max(
    fromLm,
    fromActivity,
    isoUtcToMillis(ticket.updatedOn),
    publicMessageMs,
    ticketCreatedOnSortMillis(ticket),
  );
}

/** Latest sidebar preview comment (`cx/comment/recent`, ORM, or resolved snapshot). */
function resolveSidebarPublicRecentCommentSnapshot(ticket: {
  ticketId?: string | null;
  recentComment?: TicketRecentCommentSnapshot | null;
  lastModifiedJson?: string | null;
}): TicketRecentCommentSnapshot | null {
  const ticketId = String(
    ticket.ticketId ?? ticket.recentComment?.ticketId ?? "",
  ).trim();
  const fromOrm =
    ticketId !== "" ? findLastDeliveredPublicCommentOnTicket(ticketId) : null;
  const fromOrmSnap =
    fromOrm != null
      ? snapshotFromCommentRow(fromOrm, String(fromOrm.id))
      : null;

  const api = ticket.recentComment;
  const apiSnap =
    api != null && isSidebarPublicMessageSnapshot(api) ? api : null;

  const resolved = resolveTicketRecentCommentSnapshot(ticket);
  const resolvedSnap =
    resolved != null && isSidebarPublicMessageSnapshot(resolved)
      ? resolved
      : null;

  return pickBestSidebarPreviewSnapshot(apiSnap, resolvedSnap, fromOrmSnap);
}

export function ticketRecentMessageAuthorName(ticket: {
  recentComment?: TicketRecentCommentSnapshot | null;
}): string {
  // Preview bar uses cx/recent sidebar comment only — never fall back to ticket creator.
  if (!ticketRecentMessagePreview(ticket).trim()) {
    return "";
  }
  const rc = resolveSidebarPublicRecentCommentSnapshot(ticket);
  if (rc == null) {
    return "";
  }
  return messageAuthorDisplayNameForComment(
    rc.createdByTechnicianId,
    rc.createdByContactId,
    rc.ruleId,
    rc.ruleName,
  );
}

export function ticketRecentMessagePreview(ticket: {
  recentComment?: TicketRecentCommentSnapshot | null;
}): string {
  const rc = resolveSidebarPublicRecentCommentSnapshot(ticket);
  const plain = snapshotPlainBody(rc);
  if (plain === "") {
    return "";
  }
  return _truncatedCommentPreview(plain);
}

/** Full plain-text body of the latest public message (not truncated). */
export function ticketRecentMessagePlainText(ticket: {
  recentComment?: TicketRecentCommentSnapshot | null;
}): string {
  const rc = resolveSidebarPublicRecentCommentSnapshot(ticket);
  return snapshotPlainBody(rc);
}

/** True when query matches ticket title and/or sidebar recent-message fields. */
export function ticketMatchesSidebarSearchQuery(
  ticket: {
    ticketTitle?: string | null;
    ticketTitleWithPrefix?: string;
    ticketTitleWithPrefixWithHyphen?: string;
    ticketNumberWithPrefix?: string | null;
    recentComment?: TicketRecentCommentSnapshot | null;
  },
  queryLower: string,
): boolean {
  const q = queryLower.trim();
  if (!q) {
    return true;
  }
  const parts = [
    ticket.ticketTitle,
    ticket.ticketTitleWithPrefix,
    ticket.ticketTitleWithPrefixWithHyphen,
    ticket.ticketNumberWithPrefix,
    ticketRecentMessageAuthorName(ticket),
    ticketRecentMessagePreview(ticket),
    ticketRecentMessagePlainText(ticket),
  ];
  return parts.some((p) => {
    const s = p != null ? String(p).trim() : "";
    return s !== "" && s.toLowerCase().includes(q);
  });
}

/** UTC millis of the latest sidebar comment on the ticket (for sidebar sort). */
export function ticketRecentMessageCreatedOnMillis(ticket: {
  recentComment?: TicketRecentCommentSnapshot | null;
}): number {
  const rc = resolveSidebarPublicRecentCommentSnapshot(ticket);
  if (rc == null) {
    return 0;
  }
  return isoUtcToMillis(rc.createdOn);
}

export function ticketCreatedOnSortMillis(ticket: {
  createdOn?: string | null;
  jsCreatedOn?: Date | null;
}): number {
  const fromIso = isoUtcToMillis(ticket.createdOn);
  if (fromIso > 0) {
    return fromIso;
  }
  const js = ticket.jsCreatedOn;
  if (js instanceof Date && !Number.isNaN(js.getTime())) {
    return js.getTime();
  }
  return 0;
}

function parseLastModifiedJsonForSidebar(
  lastModifiedJson?: string | null,
): Record<string, unknown> | null {
  return parseLastModifiedRecord(lastModifiedJson);
}

/** Status-only MSP edits must not reorder the Messages sidebar or shift sidebar time. */
export function isStatusOnlySidebarTicketActivity(ticket: {
  lastActionId?: number | null;
  lastModifiedJson?: string | null;
}): boolean {
  if (
    Number(ticket.lastActionId ?? 0) === Enums.TicketLastAction.StatusUpdated
  ) {
    return true;
  }
  const lm = parseLastModifiedJsonForSidebar(ticket.lastModifiedJson);
  return lm?.editedType === 8;
}

/**
 * Sidebar list order: latest sidebar comment (Messages, Approval, or TPC), or ticket creation
 * when there is no thread yet — so a newly created ticket is not buried under older threads.
 * Ticket `updatedOn` is intentionally excluded (status/assignee edits must not bubble).
 */
export function ticketSidebarActivitySortMillis(
  ticket: {
    recentComment?: TicketRecentCommentSnapshot | null;
    createdOn?: string | null;
    jsCreatedOn?: Date | null;
    lastModifiedJson?: string | null;
  },
  latestPublicMessageMs = 0,
): number {
  const publicMs =
    latestPublicMessageMs > 0
      ? latestPublicMessageMs
      : ticketRecentMessageCreatedOnMillis(ticket);
  return Math.max(publicMs, ticketCreatedOnSortMillis(ticket));
}

/** ISO timestamp of the latest sidebar comment (for sidebar relative time). */
export function ticketRecentMessageCreatedOnIso(ticket: {
  recentComment?: TicketRecentCommentSnapshot | null;
  lastModifiedJson?: string | null;
  lastActionId?: number | null;
}): string | null {
  if (isStatusOnlySidebarTicketActivity(ticket)) {
    const rc = resolveSidebarPublicRecentCommentSnapshot(ticket);
    if (rc?.createdOn != null && String(rc.createdOn).trim() !== "") {
      return String(rc.createdOn);
    }
    return null;
  }
  const rc = resolveSidebarPublicRecentCommentSnapshot(ticket);
  if (rc == null) {
    return null;
  }
  const raw = rc.createdOn;
  if (raw == null || String(raw).trim() === "") {
    return null;
  }
  return String(raw);
}
